import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Badge,
  Alert,
  Spinner,
  ListGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUser, faCalendar, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { useParams, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';

const GroupDetails = () => {
  const { id } = useParams();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (id) {
      fetchGroupDetails();
    }
  }, [id]);

  const fetchGroupDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/groups/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch group details');
      }

      const data = await response.json();
      setGroup(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success">Active</Badge>
    ) : (
      <Badge bg="warning">Inactive</Badge>
    );
  };

  const getMemberRoleBadge = (role) => {
    return role === 'moderator' ? (
      <Badge bg="primary">Moderator</Badge>
    ) : (
      <Badge bg="secondary">Member</Badge>
    );
  };

  const getMemberStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">Active</Badge>;
      case 'invited':
        return <Badge bg="warning">Invited</Badge>;
      case 'removed':
        return <Badge bg="danger">Removed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}
      </Alert>
    );
  }

  if (!group) {
    return (
      <Alert variant="warning">
        Group not found
      </Alert>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/admin/groups">Groups</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {group.name}
              </li>
            </ol>
          </nav>
          <h2 className="h4">{group.name}</h2>
          <p className="mb-0">Group details and member management</p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <Link to={`/admin/groups/${group.id}/edit`} className="btn btn-primary">
            Edit Group
          </Link>
          <Button
            variant="outline-danger"
            className="ms-2"
            onClick={async () => {
              const token = localStorage.getItem('token') || '';
              const result = await Swal.fire({
                title: 'Report group',
                html: `
                  <div style="text-align:left;">
                    <label><b>Reason</b></label>
                    <input id="swal-report-reason-group" class="swal2-input" placeholder="e.g. Harassment, Spam..." />
                    <label style="margin-top:10px;"><b>Notes (optional)</b></label>
                    <textarea id="swal-report-notes-group" class="swal2-textarea" placeholder="Additional details..."></textarea>
                  </div>
                `,
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: 'Submit report',
                cancelButtonText: 'Cancel',
                heightAuto: false,
                preConfirm: () => {
                  const reason = (document.getElementById('swal-report-reason-group')?.value || '').toString().trim();
                  const notes = (document.getElementById('swal-report-notes-group')?.value || '').toString().trim();
                  if (!reason || reason.length < 3) {
                    Swal.showValidationMessage('Reason is required (min 3 characters).');
                    return false;
                  }
                  return { reason, notes };
                }
              });

              if (!result.isConfirmed) return;
              try {
                const payload = result.value;
                const res = await fetch('/api/reports', {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    entity_type: 'group',
                    entity_id: Number(id),
                    reason: payload.reason,
                    notes: payload.notes || null
                  })
                });

                const json = await res.json();
                if (!res.ok || json.error) throw new Error(json.message || 'Failed to submit report');
                await Swal.fire({ title: 'Report submitted', text: 'Thank you.', icon: 'success', heightAuto: false });
              } catch (e) {
                await Swal.fire({ title: 'Error', text: e?.message || 'Failed to submit report', icon: 'error', heightAuto: false });
              }
            }}
          >
            Report
          </Button>
        </div>
      </div>

      <Row>
        {/* Group Information */}
        <Col lg={4} className="mb-4">
          <Card className="border-0 shadow">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Group Information</h5>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item className="px-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">Status</span>
                    {getStatusBadge(group.is_active)}
                  </div>
                </ListGroup.Item>
                <ListGroup.Item className="px-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">Created</span>
                    <span>{formatDate(group.createdAt)}</span>
                  </div>
                </ListGroup.Item>
                {group.subscription_start_date && (
                  <ListGroup.Item className="px-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold">Subscription Start</span>
                      <span>{formatDate(group.subscription_start_date)}</span>
                    </div>
                  </ListGroup.Item>
                )}
                {group.subscription_end_date && (
                  <ListGroup.Item className="px-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold">Subscription End</span>
                      <span>{formatDate(group.subscription_end_date)}</span>
                    </div>
                  </ListGroup.Item>
                )}
              </ListGroup>
            </Card.Body>
          </Card>

          {/* Creator Information */}
          <Card className="border-0 shadow mt-4">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Creator</h5>
            </Card.Header>
            <Card.Body>
              {group.creator && (
                <div className="d-flex align-items-center">
                  <div className="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-3">
                    <FontAwesomeIcon icon={faUser} className="text-white" />
                  </div>
                  <div>
                    <h6 className="mb-0">{group.creator.name || 'Unknown'}</h6>
                    <small className="text-muted">{group.creator.email}</small>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Members List */}
        <Col lg={8} className="mb-4">
          <Card className="border-0 shadow">
            <Card.Header className="bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Group Members</h5>
                <Badge bg="secondary">
                  {group.groupMembers?.filter(member => member.status === 'active').length || 0} / 5
                </Badge>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {!group.groupMembers || group.groupMembers.length === 0 ? (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faUsers} className="text-muted mb-2" size="2x" />
                  <p className="text-muted">No members found</p>
                </div>
              ) : (
                <Table responsive className="table-flush mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.groupMembers.map((member) => (
                      <tr key={member.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="avatar-sm bg-secondary rounded-circle d-flex align-items-center justify-content-center me-3">
                              <FontAwesomeIcon icon={faUser} className="text-white" />
                            </div>
                            <div>
                              <h6 className="mb-0">{member.user?.name || 'Unknown'}</h6>
                              <small className="text-muted">{member.user?.email}</small>
                            </div>
                          </div>
                        </td>
                        <td>{getMemberRoleBadge(member.role)}</td>
                        <td>{getMemberStatusBadge(member.status)}</td>
                        <td>
                          {member.joined_at ? formatDate(member.joined_at) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default GroupDetails;
